import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  Component, EventEmitter, Input, Output, ViewChild,
  computed, inject, signal,
} from '@angular/core';

import {
  BadgeComponent, ButtonComponent, CardComponent, ConfirmDialogComponent,
  EmptyStateComponent, SpinnerComponent, ToastService,
} from '@echospaces-web-monorepo/es-shared-ui';

import { ContentService, type Content } from '../content/content.service';
import { LinkContentModalComponent } from './link-content-modal.component';
import { LinkService, type Link, type PlaceholderUsedAs } from './link.service';
import { PlaceholderService, type Placeholder, type PlaceholderType } from './placeholder.service';

const TYPE_ICONS: Record<PlaceholderType, string> = {
  visible: '🖼', audible: '🔊', readable: '📄', usable: '🧊', reactive: '✨',
};

/**
 * Placeholders list for the currently selected space (subtree-scoped via
 * the backend). Per placeholder card: show the type, slug, slug-path,
 * status, and either the currently linked content (with an unlink button)
 * or a "+ Link content" action that opens the LinkContentModal.
 *
 * The tab also loads the island's link table (one query, full subtree)
 * and the island's content library so the picker has data without extra
 * round-trips.
 */
@Component({
  selector: 'es-space-placeholders-tab',
  imports: [
    BadgeComponent, ButtonComponent, CardComponent, ConfirmDialogComponent,
    EmptyStateComponent, SpinnerComponent, LinkContentModalComponent,
  ],
  template: `
    @if (placeholders.loading()) {
      <es-spinner label="Loading placeholders…"></es-spinner>
    } @else if (placeholders.placeholders().length === 0) {
      <es-empty-state
        title="No placeholders"
        description="Placeholders are created by Unreal developers via the editor plugin. None exist in this space (or its subtree) yet."
        icon="📌"
      ></es-empty-state>
    } @else {
      <div class="space-y-3">
        @for (placeholder of placeholders.placeholders(); track placeholder.id) {
          <es-card>
            <div class="flex items-start gap-3">
              <span class="text-2xl shrink-0">{{ TYPE_ICONS[placeholder.type] }}</span>

              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <p class="font-medium truncate">{{ placeholder.displayName ?? placeholder.slug }}</p>
                  <es-badge [label]="placeholder.type" variant="secondary"></es-badge>
                  @if (placeholder.placeholderStatus !== 'active') {
                    <es-badge [label]="placeholder.placeholderStatus" variant="warning"></es-badge>
                  }
                </div>
                @if (placeholder.slugPath) {
                  <p class="text-xs text-[var(--text-secondary)] font-mono truncate mt-0.5">
                    {{ placeholder.slugPath }}
                  </p>
                }

                @if (linkFor(placeholder.id); as link) {
                  <!-- Linked state -->
                  <div class="mt-2 flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--bg-secondary)] border border-[var(--border)]">
                    <span class="text-sm">↪</span>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium truncate">
                        {{ contentLabel(link.contentId) }}
                      </p>
                      <p class="text-xs text-[var(--text-secondary)]">
                        used as <span class="font-medium">{{ link.usedAs }}</span>
                      </p>
                    </div>
                  </div>
                }
              </div>

              <div class="shrink-0 flex flex-col gap-1 items-end">
                @if (linkFor(placeholder.id)) {
                  <es-button
                    variant="ghost"
                    size="sm"
                    [disabled]="!canEdit"
                    (clicked)="askUnlink(placeholder)"
                  >Unlink</es-button>
                } @else {
                  <es-button
                    variant="secondary"
                    size="sm"
                    [disabled]="!canEdit || placeholder.placeholderStatus === 'deprecated'"
                    (clicked)="openLinkModal(placeholder)"
                  >+ Link content</es-button>
                }
              </div>
            </div>
          </es-card>
        }
      </div>
    }

    <!-- Link modal -->
    <es-link-content-modal
      #linkModal
      [open]="linkModalOpen()"
      [contents]="content.contents()"
      [placeholder]="targetPlaceholder()"
      (closed)="linkModalOpen.set(false)"
      (submitted)="onLinkSubmit($event)"
    ></es-link-content-modal>

    @if (placeholderToUnlink(); as target) {
      <es-confirm-dialog
        [open]="true"
        title="Unlink content?"
        [message]="'Remove the content link from placeholder ' + (target.displayName ?? target.slug) + '? The placeholder will become empty.'"
        confirmLabel="Unlink"
        [destructive]="true"
        (confirmed)="confirmUnlink(target)"
        (cancelled)="placeholderToUnlink.set(null)"
      ></es-confirm-dialog>
    }
  `,
})
export class SpacePlaceholdersTabComponent implements OnChanges {
  @Input({ required: true }) spaceId!: string;
  /** Island root the selected space belongs to — drives the link list + content library scope. */
  @Input({ required: true }) islandSpaceId!: string;
  /** Mirrors `canEditSelected` from the parent — gates the Link / Unlink actions. */
  @Input() canEdit = false;

  @Output() linksChanged = new EventEmitter<void>();

  @ViewChild('linkModal') linkModal?: LinkContentModalComponent;

  readonly placeholders = inject(PlaceholderService);
  readonly links = inject(LinkService);
  readonly content = inject(ContentService);
  private readonly toast = inject(ToastService);

  readonly TYPE_ICONS = TYPE_ICONS;

  readonly linkModalOpen = signal(false);
  readonly targetPlaceholder = signal<Placeholder | null>(null);
  readonly placeholderToUnlink = signal<Placeholder | null>(null);

  /**
   * Map contentId → Content for label lookups on linked placeholders. The
   * content library was already loaded by the parent (or this tab on
   * mount), so this is a cheap derived signal.
   */
  readonly contentById = computed<Map<string, Content>>(() => {
    const map = new Map<string, Content>();
    for (const c of this.content.contents()) map.set(c.id, c);
    return map;
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['spaceId'] && this.spaceId) {
      this.placeholders.loadForSpace(this.spaceId);
    }
    if (changes['islandSpaceId'] && this.islandSpaceId) {
      this.links.loadForIsland(this.islandSpaceId);
      // The content library powers the link-modal's typeahead. Backend
      // lists root-attached content; subtree mode is a future server-side
      // option but for now the island root is the practical default.
      this.content.loadContents(this.islandSpaceId);
    }
  }

  linkFor(placeholderId: string): Link | undefined {
    return this.links.byPlaceholderId().get(placeholderId);
  }

  contentLabel(contentId: string): string {
    const c = this.contentById().get(contentId);
    return c?.title ?? `Content ${contentId.slice(0, 8)}…`;
  }

  openLinkModal(placeholder: Placeholder): void {
    this.targetPlaceholder.set(placeholder);
    this.linkModalOpen.set(true);
  }

  async onLinkSubmit(payload: { contentId: string; usedAs: PlaceholderUsedAs }) {
    const placeholder = this.targetPlaceholder();
    if (!placeholder) return;
    try {
      await this.links.createLink(this.islandSpaceId, placeholder.id, payload.contentId, {
        placeholderUsedAs: payload.usedAs,
      });
      this.linkModal?.finishSubmit();
      this.toast.success('Content linked');
      this.linkModalOpen.set(false);
      this.linksChanged.emit();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to link';
      this.linkModal?.finishSubmit(message);
    }
  }

  askUnlink(placeholder: Placeholder): void {
    this.placeholderToUnlink.set(placeholder);
  }

  async confirmUnlink(placeholder: Placeholder): Promise<void> {
    this.placeholderToUnlink.set(null);
    try {
      await this.links.removeLink(this.islandSpaceId, placeholder.id);
      this.toast.success('Content unlinked');
      this.linksChanged.emit();
    } catch {
      this.toast.error('Failed to unlink');
    }
  }
}
